import { useMemo, useState } from "react";
import { IonSearchbar, IonSkeletonText } from "@ionic/react";
import { DateTime } from "luxon";
import { useAuth } from "pages";
import { Alert, Colors, fontMedium, fontXSmall } from "swing-components";

import { useBudgets } from "~components/FormFields";
import { currentAndNextAcademicYear } from "~components/Molecules/SchoolProfile/SchoolClosures/utils";
import { translate } from "~utils";

import { BudgetRow, tableRowCss } from "./BudgetRow";

export function Budgets() {
  const [searchTerm, setSearchTerm] = useState("");
  const { userInfo } = useAuth();

  const years = currentAndNextAcademicYear(DateTime.now());

  const yearsString = `${years[0].startDate.year.toString().slice(-2)}-${years[0].endDate.year.toString().slice(-2)}`;

  const { isLoading, data: budgets } = useBudgets({
    showSuperadminBudgets: userInfo?.isSuperAdmin,
    hideInactive: false,
    includeSpend: true,
  });

  const filteredAndSortedBudgets = useMemo(() => {
    if (!budgets) return [];
    return budgets
      .filter((budget) => budget.name.toLowerCase().includes(searchTerm.toLowerCase()))
      .sort((a, b) => {
        if (a.status !== b.status) {
          return a.status === "ACTIVE" ? -1 : 1;
        }
        return a.name.localeCompare(b.name);
      });
  }, [budgets, searchTerm]);

  const canEdit = userInfo.isBillingAdmin || userInfo.isSuperAdmin || false;

  if (isLoading) {
    return (
      <div css={{ padding: 8, display: "flex", flexDirection: "column", gap: 8 }}>
        {Array.from({ length: 6 }).map((_, i) => (
          <IonSkeletonText key={i} animated css={{ height: 40 }} />
        ))}
      </div>
    );
  }

  if (!budgets) {
    return <div>{translate("BUDGETS_EMPTY_STATE")}</div>;
  }

  return (
    <div css={{ padding: "16px 0" }}>
      <Alert color="disclaimer">{translate("BUDGETS_EMAIL_DISCLAIMER")}</Alert>
      <IonSearchbar
        value={searchTerm}
        debounce={400}
        onIonInput={(e) => setSearchTerm(e.target.value as string)}
        placeholder={translate("BUDGETS_SEARCH_PLACEHOLDER")}
        css={{
          boxShadow: "none",
          "--box-shadow": "none",
          border: "solid 1px grey",
          borderRadius: 8,
          marginTop: 16,
          padding: "7px",
        }}
      />

      <div
        css={{
          maxWidth: 900,
          margin: "16px 0",
          overflowX: "scroll",
          background: "white",
          borderRadius: 8,
          border: `solid 1px ${Colors.slate200}`,
        }}
      >
        {/* Header row */}
        <div
          css={{
            ...tableRowCss,
            ...fontMedium({ fontWeight: 600 }),
          }}
        >
          <span>{translate("BUDGETS_LABEL")}</span>
          <span>{translate("BUDGETS_SITE_LABEL")}</span>
          <div>
            {translate("BUDGETS_SPEND_LABEL")}
            <p css={{ ...fontXSmall(), margin: 0 }}>{translate("BUDGETS_SY_LABEL")}</p>
          </div>
          <div>
            <span>{translate("BUDGETS_ANNUAL_LIMIT_LABEL")}</span>
            <p css={{ ...fontXSmall(), margin: 0 }}>
              {translate("BUDGETS_ANNUAL_LIMIT_SUB", { years: yearsString })}
            </p>
          </div>
          <div>
            {translate("BUDGETS_UTILIZATION_LABEL")}
            <p css={{ ...fontXSmall(), margin: 0 }}>{translate("BUDGETS_PCT_LABEL")}</p>
          </div>
          <div>Status</div>
          <div></div>
        </div>
        {filteredAndSortedBudgets.map((budget) => {
          return <BudgetRow canEdit={canEdit} budget={budget} key={budget.id} />;
        })}
      </div>
      <span>{translate("BUDGETS_RESET_LABEL")}</span>
    </div>
  );
}
